/**
 * Trip actions, reviews and partner status (P5, P13): schemas and pure mappers.
 * Endpoints: GET /api/reservations/:id/cancel-preview, PATCH /api/reservations/:id/client-cancel,
 * PATCH /api/client/activity-reservations/:id/cancel, POST /api/reservations/:id/request-extension,
 * GET /api/ratings/client, GET /api/client/auth/partner/status.
 */
import { z } from 'zod';
import { ns, translate } from '@/i18n';
import type {
  CancelOutcome,
  CancelPreview,
  CancelRequest,
  PartnerStatus,
  Review,
  TripExtension,
  VehicleRatingCriterion,
} from '../../domain';
import { firstImage, VEHICLE_PLACEHOLDER } from './media';

const n = z.number().nullish();
const s = z.string().nullish();
const b = z.boolean().nullish();

/* ── Cancel preview and outcome ─────────────────────────────────────────── */

export const CancelPreviewSchema = z
  .object({
    success: b,
    data: z
      .object({
        cancellable: z.boolean(),
        code: s,
        tier: s,
        reason: s,
        refundAmount: n,
        currency: s,
        pct: n,
        paidOnline: n,
        released: b,
        freeCancelUntil: s,
      })
      .passthrough(),
  })
  .passthrough();

export function toCancelPreview(body: z.infer<typeof CancelPreviewSchema>): CancelPreview {
  const d = body.data;
  if (!d.cancellable) {
    return {
      cancellable: false,
      blocked: d.code === 'start_date_reached' ? 'start_date_reached' : 'already_terminal',
      refundAmount: 0,
      currency: (d.currency ?? 'MAD').toUpperCase(),
      pct: 0,
      paidOnline: 0,
      released: false,
      noOnlinePayment: false,
    };
  }
  return {
    cancellable: true,
    refundAmount: Number(d.refundAmount) || 0,
    currency: (d.currency ?? 'MAD').toUpperCase(),
    pct: Number(d.pct) || 0,
    paidOnline: Number(d.paidOnline) || 0,
    released: d.released === true,
    noOnlinePayment: d.reason === 'no_online_payment',
    freeUntil: d.freeCancelUntil ?? undefined,
  };
}

/** `refund` of the vehicle cancel (`refundReservation`) or of the activity cancel (`{...policy, ...refund}`). */
export const RefundResultSchema = z
  .object({
    refunded: b,
    released: b,
    amount: n,
    pct: n,
    refundPercentage: n,
    status: s,
    reason: s,
    currency: s,
  })
  .passthrough();

export const VehicleCancelSchema = z
  .object({
    success: b,
    message: s,
    refund: RefundResultSchema.nullish(),
    data: z.object({ payment: z.object({ currency: s }).passthrough().nullish() }).passthrough().nullish(),
  })
  .passthrough();

export const ActivityCancelSchema = z.object({ refund: RefundResultSchema.nullish() }).passthrough();

export function toCancelOutcome(
  refund: z.infer<typeof RefundResultSchema> | null | undefined,
  fallbackCurrency = 'MAD',
): CancelOutcome {
  if (!refund || refund.reason === 'no_online_payment') {
    return {
      released: false,
      refunded: false,
      pending: false,
      amount: 0,
      pct: 0,
      currency: fallbackCurrency.toUpperCase(),
      noOnlinePayment: true,
    };
  }
  const amount = Number(refund.amount) || 0;
  const released = refund.released === true || refund.reason === 'released_hold';
  return {
    released,
    // Vehicles say `refunded: true`; experiences say `status: 'completed'` (refundService.processRefund).
    refunded: !released && (refund.refunded === true || refund.status === 'completed') && amount > 0,
    pending: !released && refund.status === 'pending' && amount > 0,
    amount,
    pct: Number(refund.pct ?? refund.refundPercentage) || 0,
    currency: (refund.currency ?? fallbackCurrency).toUpperCase(),
    noOnlinePayment: false,
  };
}

/** Body of `PATCH /api/reservations/:id/client-cancel`: the note only when given, the answers only when asked. */
export function clientCancelBody(r: CancelRequest): Record<string, unknown> {
  const body: Record<string, unknown> = { reason: r.reason };
  const note = r.note?.trim().slice(0, 500);
  if (note) body.note = note;
  if (r.alertAnswers) body.alertAnswers = r.alertAnswers;
  return body;
}

/* ── Extension ──────────────────────────────────────────────────────────── */

export const ExtensionResponseSchema = z
  .object({
    success: b,
    message: s,
    data: z
      .object({
        extensionRequest: z
          .object({ requestedReturnDate: s, newTotalPrice: n, priceDifference: n, status: s })
          .passthrough()
          .nullish(),
      })
      .passthrough()
      .nullish(),
  })
  .passthrough();

export function toExtension(
  body: z.infer<typeof ExtensionResponseSchema>,
  requested: { returnDate: string; returnTime: string },
): TripExtension {
  const e = body.data?.extensionRequest;
  return {
    status: 'pending',
    // The date the client picked (the server echoes it as UTC midnight of the same day).
    returnDate: requested.returnDate,
    returnTime: requested.returnTime,
    newTotal: typeof e?.newTotalPrice === 'number' ? e.newTotalPrice : undefined,
    difference: typeof e?.priceDifference === 'number' ? e.priceDifference : undefined,
  };
}

/* ── Reviews («Mes avis») ───────────────────────────────────────────────── */

const RefName = z.union([z.string(), z.object({ name: s }).passthrough()]).nullish();
export const ClientReviewSchema = z
  .object({
    _id: z.string(),
    carId: z
      .union([
        z.string(),
        z
          .object({
            _id: z.string(),
            brand: RefName,
            subBrand: RefName,
            name: s,
            city: s,
            images: z.array(z.unknown()).nullish(),
            agencyId: z.union([z.string(), z.object({ agencyName: s, name: s }).passthrough()]).nullish(),
          })
          .passthrough(),
      ])
      .nullish(),
    service: n,
    carCondition: n,
    valueForMoney: n,
    carPerformance: n,
    cleanliness: n,
    averageRating: n,
    comment: s,
    createdAt: s,
  })
  .passthrough();
export const ClientReviewListSchema = z.array(ClientReviewSchema);

const nameOf = (v: unknown): string =>
  typeof v === 'string' ? (/^[a-f\d]{24}$/i.test(v) ? '' : v) : ((v as { name?: string } | null)?.name ?? '');

const CRITERIA: VehicleRatingCriterion[] = ['service', 'carCondition', 'valueForMoney', 'carPerformance', 'cleanliness'];

export function toReview(r: z.infer<typeof ClientReviewSchema>, baseUrl: string): Review {
  const car = r.carId && typeof r.carId === 'object' ? r.carId : null;
  // `brand subBrand`, never `car.name` (the registration plate field) unless nothing else exists.
  const title = [nameOf(car?.brand), nameOf(car?.subBrand)].filter(Boolean).join(' ') || car?.name || translate(ns.labels, 'vehicle');
  const agency =
    car?.agencyId && typeof car.agencyId === 'object' ? (car.agencyId.agencyName ?? car.agencyId.name ?? '') : '';
  const scores: Review['scores'] = {};
  for (const k of CRITERIA) if (typeof r[k] === 'number') scores[k] = r[k] as number;
  const values = Object.values(scores);
  const average =
    typeof r.averageRating === 'number'
      ? r.averageRating
      : values.length
        ? values.reduce((a, x) => a + x, 0) / values.length
        : 0;
  return {
    id: r._id,
    vehicleId: car?._id ?? (typeof r.carId === 'string' ? r.carId : undefined),
    title,
    image: firstImage(baseUrl, car?.images) || VEHICLE_PLACEHOLDER,
    agencyLine: [agency, car?.city].filter(Boolean).join(' · '),
    createdAt: r.createdAt ?? '',
    average,
    scores,
    comment: r.comment ?? '',
  };
}

/* ── Partner status ─────────────────────────────────────────────────────── */

export const PartnerStatusSchema = z.object({ partnerStatus: s, dashboardUrl: s }).passthrough();

export function toPartnerStatus(body: z.infer<typeof PartnerStatusSchema>): PartnerStatus {
  const st = body.partnerStatus;
  const status = st === 'approved' || st === 'requested' || st === 'rejected' ? st : 'none';
  const url = body.dashboardUrl?.trim();
  return { status, dashboardUrl: status === 'approved' && url && /^https?:\/\//.test(url) ? url : null };
}
